import { Icon } from "@atoms";
import { getAcceptedGroups, useAppSelector } from "@lib/store";
import { cn } from "@utils";
import { t } from "i18next";
import { Fragment } from "react";
import { Link, matchRoutes, useLocation } from "react-router-dom";

export const useConnectBreadCrumbs = () => {
  const location = useLocation();
  const groups = useAppSelector(getAcceptedGroups);

  const connectedUsers = useAppSelector(state => state.data.connections.connectedUsers);

  // Since we dont have any proper route config (we use the old way of defining routes),
  // we need to define it here to use the matchRoutes util
  const routeConfig = [
    { path: "connect" },
    { path: "connect/connections/:userId" },
    { path: "connect/groups/:groupId" },
    { path: "connect/groups/:groupId/settings" },
    { path: "connect/groups/:groupId/members/:userId" },
  ];

  const pathParts = location.pathname.split("/").filter(Boolean);
  const completePaths = pathParts.map((_, index) => `/${pathParts.slice(0, index + 1).join("/")}`);

  const filteredPaths = completePaths.filter(path => {
    const matches = matchRoutes(routeConfig, path);
    return matches && matches.length > 0;
  });

  const getLabel = (fullPath: string) => {
    const matches = matchRoutes(routeConfig, fullPath);
    const match = matches?.at(0);

    switch (match?.route?.path) {
      case "connect":
        return t("connection:ConnectedCoworkers");
      case "connect/groups/:groupId": {
        const group = groups.find(g => match.params.groupId === g.id);
        return group?.name || t("groups:UnknownGroup");
      }
      case "connect/groups/:groupId/settings":
        return "Group settings";
      case "connect/groups/:groupId/members/:userId": {
        const group = groups.find(g => match.params.groupId === g.id);
        const member = group?.members.find(m => m.user.id === match.params.userId);
        return member?.user.name || t("groups:UnknownUser");
      }
      case "connect/connections/:userId": {
        const connectedUser = connectedUsers.entities[match.params.userId ?? ""];
        return connectedUser?.name || t("groups:UnknownUser");
      }
      default:
        return null;
    }
  };

  const crumbs = filteredPaths.map((path, index) => {
    const pathSegments = path.split("/").filter(Boolean);
    const lastPathPart = pathSegments.at(-1);

    return {
      label: getLabel(path),
      path: path,
    };
  });

  return {
    crumbs,
    paths: filteredPaths,
  };
};

export const BreadCrumbs = ({ className }: { className?: string }) => {
  const { crumbs, paths } = useConnectBreadCrumbs();

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn(
        "text-mapiq-black-500 text-sm md:text-[15px] md:leading-6 flex gap-1 items-center flex-wrap",
        className,
      )}>
      {crumbs.map((crumb, i) => (
        <Fragment key={crumb.path}>
          {i > 0 && (
            <Icon
              icon="caretRight"
              className="size-4"
            />
          )}
          {i === paths.length - 1 ? (
            <span className="font-semibold">{crumb.label}</span>
          ) : (
            <Link
              className="text-mapiq-black-500 whitespace-nowrap"
              to={crumb.path}>
              {crumb.label}
            </Link>
          )}
        </Fragment>
      ))}
    </nav>
  );
};
